// Slice-internal Konva renderer for ellipses (TR4).
//
// Extracted 1:1 from `objects/board-object-renderer.ts`
// (createEllipseNode / updateEllipseNode branch). The ellipse slice produces
// a `Konva.Group` containing a `Konva.Ellipse` child — divergence from the
// rectangle slice which yields a `Konva.Rect` directly. The shape name is
// redefined locally per OQ1 (no cross-slice import of the constant).

import Konva from 'konva';
import type { SliceRenderContext } from '../../core/object-registry.js';
import type { EllipseObject } from './ellipse.interface.js';
import { konvaDashPattern, konvaDropShadowAttrs } from '../../objects/konva-decoration.helper.js';

const ELLIPSE_SHAPE_NAME = 'ellipse-shape';

/**
 * Paint attributes for the inner Konva.Ellipse — shared by create/update.
 * `fillEnabled` is always on so the whole body stays hittable (centre drag);
 * a 'transparent' fill is simply invisible on the scene canvas. The optional
 * effects reuse the shared border-style / box-shadow / opacity widgets.
 */
function shapePaintAttrs(obj: EllipseObject): Konva.ShapeConfig {
  return {
    fill: obj.fill || 'transparent',
    fillEnabled: true,
    stroke: obj.strokeColor,
    strokeWidth: obj.strokeWidth,
    opacity: obj.backgroundOpacity ?? 1,
    dash: konvaDashPattern(obj.strokeStyle, obj.strokeWidth),
    ...konvaDropShadowAttrs(obj.boxShadow === 'on'),
  };
}

/** Build a fresh Konva.Group with a centred Konva.Ellipse child. */
export function createNode(obj: EllipseObject, _ctx: SliceRenderContext): Konva.Group {
  const group = new Konva.Group({
    id: obj.id,
    x: obj.x,
    y: obj.y,
    width: obj.width,
    height: obj.height,
    rotation: obj.rotation,
    name: 'ellipse-obj',
  });

  const shape = new Konva.Ellipse({
    name: ELLIPSE_SHAPE_NAME,
    x: obj.width / 2,
    y: obj.height / 2,
    radiusX: obj.width / 2,
    radiusY: obj.height / 2,
    ...shapePaintAttrs(obj),
  });

  group.add(shape);
  return group;
}

/** Apply changed properties from an updated EllipseObject onto its inner shape. */
export function updateNode(node: Konva.Node, obj: EllipseObject, _ctx: SliceRenderContext): void {
  if (!(node instanceof Konva.Group)) return;
  // Base geometry (group x/y/width/height/rotation) is applied centrally by
  // `applyBaseGeometry`; this method only recomputes the inner ellipse's
  // local radii and paint from the new size.
  const shape = node.findOne<Konva.Ellipse>(`.${ELLIPSE_SHAPE_NAME}`);
  if (!shape) return;
  shape.setAttrs({
    x: obj.width / 2,
    y: obj.height / 2,
    radiusX: obj.width / 2,
    radiusY: obj.height / 2,
    ...shapePaintAttrs(obj),
  });
}
